import React, { useState, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { Clock, ClockAlert, ClockFading } from "lucide-react";
import { format, isPast, isToday, isYesterday, isTomorrow } from "date-fns";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Calendar } from "@/components/ui/calendar";
import { useIssuesStore } from "@/store/zustand/issues-store";
import { toast } from "sonner";
import { CheckIcon } from "lucide-react";
import { ShortcutHandler } from "@/components/providers/ShortcutProvider";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";

interface DueDateBadgeProps {
  dueDate?: string | Date | null;
  className?: string;
  issueId?: string;
  variant?: "outline" | "default" | "text-dark";
  onDueDateChange?: (dueDate: Date | null) => void;
  ignoreOverdueIcon?: boolean;
  allowOverdueStyling?: boolean;
  iconColor?: string;
}

export const DueDateBadge: React.FC<DueDateBadgeProps> = ({
  dueDate,
  className = "",
  issueId,
  variant = "default",
  onDueDateChange,
  ignoreOverdueIcon = false,
  allowOverdueStyling = false,
  iconColor = "var(--text-primary)",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const updateIssueDueDate = useIssuesStore(
    (state) => state.updateIssueDueDate
  );
  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);

  const handleDueDateChange = async (date: Date | undefined) => {
    if (onDueDateChange) {
      onDueDateChange(date ?? null);
      return;
    }

    if (!issueId) {
      toast.error("Could not update due date");
      return;
    }

    try {
      updateIssueDueDate(supabaseClient, issueId, date ?? null);
      toast.success(
        date === undefined
          ? "Due date removed"
          : `Due date set to ${format(date, "MMM d")}`
      );
      setIsOpen(false);
    } catch (error) {
      console.error("Failed to update due date:", error);
      toast.error("Failed to update due date");
    }
  };

  const dueDateObj = dueDate ? new Date(dueDate) : null;
  const isOverdue = dueDateObj
    ? isPast(dueDateObj) && !isToday(dueDateObj)
    : false;

  const BadgeContent = () => (
    <div className="flex items-center gap-1">
      {dueDate && ignoreOverdueIcon && (
        <Clock className="w-3.5 h-3.5 flex-shrink-0" color={iconColor} />
      )}

      {!ignoreOverdueIcon &&
        (!dueDate ? (
          <ClockFading
            className="w-3.5 h-3.5 flex-shrink-0"
            color={iconColor}
          />
        ) : isOverdue ? (
          <>
            <ClockAlert className="w-3.5 h-3.5 flex-shrink-0" />
            {isYesterday(new Date(dueDate))
              ? "Yesterday"
              : format(new Date(dueDate), "MMM d")}
          </>
        ) : (
          <>
            <Clock className="w-3.5 h-3.5 flex-shrink-0" color={iconColor} />
            {isToday(new Date(dueDate))
              ? "Today"
              : isTomorrow(new Date(dueDate))
              ? "Tomorrow"
              : format(new Date(dueDate), "MMM d")}
          </>
        ))}
    </div>
  );

  const baseSpanClassName =
    "inline-flex items-center justify-center border rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 border text-xs text-secondary-n-400 cursor-pointer";

  const textDarkDropdownNode = (
    <button
      className={cn(
        `${baseSpanClassName} ${className}`,
        isOverdue && allowOverdueStyling && "text-destructive"
      )}
      data-state="closed"
      aria-label={`Due date: ${
        dueDate ? format(new Date(dueDate), "MMM d") : "No due date"
      }`}
    >
      {dueDate ? (
        <span
          className={`${baseSpanClassName}
            gap-1 w-full h-7 px-2 bg-secondary-0 hover:bg-secondary-n-800 border-border-primary
          `}
        >
          {isOverdue && !ignoreOverdueIcon ? (
            <ClockAlert
              className={cn(
                "w-3.5 h-3.5 flex-shrink-0",
                isOverdue && allowOverdueStyling && "text-destructive"
              )}
            />
          ) : (
            <Clock className="w-3.5 h-3.5 flex-shrink-0" color={iconColor} />
          )}
          <span
            className={cn(
              "text-xs font-medium text-text-primary",
              isOverdue && allowOverdueStyling && "text-destructive"
            )}
          >
            {isYesterday(new Date(dueDate))
              ? "Yesterday"
              : isToday(new Date(dueDate))
              ? "Today"
              : isTomorrow(new Date(dueDate))
              ? "Tomorrow"
              : format(new Date(dueDate), "MMM d")}
          </span>
        </span>
      ) : (
        <span
          className={`${baseSpanClassName} gap-1 w-full h-7 px-2 bg-secondary-0 hover:bg-secondary-n-800 border-secondary-n-900`}
        >
          <ClockFading
            className="w-3.5 h-3.5 flex-shrink-0"
            color={iconColor}
          />
          <span className="text-xs font-medium text-text-primary">
            Due date
          </span>
        </span>
      )}
    </button>
  );

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
        <div>
          {variant === "text-dark" ? (
            textDarkDropdownNode
          ) : (
            <Badge
              variant="outline"
              className={cn(
                "h-6 py-0 px-2 text-xs font-normal flex items-center gap-1 cursor-pointer bg-secondary-n-950",
                !dueDate && "text-muted-foreground",
                dueDate && !isOverdue && "text-white",
                dueDate &&
                  (isOverdue || allowOverdueStyling) &&
                  "text-destructive",
                "transition-colors hover:bg-secondary-n-800",
                className
              )}
            >
              <BadgeContent />
            </Badge>
          )}
        </div>
      </DropdownMenuTrigger>
      {isOpen && (
        <ShortcutHandler enabled={true}>
          <DropdownMenuContent
            ref={menuRef}
            className="w-auto p-1.5"
            align="start"
            onClick={(e) => e.stopPropagation()}
          >
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleDueDateChange(undefined);
              }}
              className="px-2 py-1.5"
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2">
                  <ClockFading className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>No due date</span>
                </div>
                {!dueDate && <CheckIcon className="size-3.5" />}
              </div>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <div className="p-0" onClick={(e) => e.stopPropagation()}>
              <Calendar
                mode="single"
                selected={dueDateObj || undefined}
                onSelect={(date) => handleDueDateChange(date)}
                initialFocus
              />
            </div>
          </DropdownMenuContent>
        </ShortcutHandler>
      )}
    </DropdownMenu>
  );
};

export default DueDateBadge;
